import React, { useContext, useState } from 'react';
import {
  Checkbox,
  CheckboxProps,
  ExpandableSection,
  Icon,
  ButtonProps,
  Box,
  BoxProps,
} from '@amzn/awsui-components-core';
import { FilterCategory, filterOptionToLabelMap, Filter } from './ContentFilters';
import { getFilterOptionsKeys, sortPublicationFilter } from '../utils/ContentFilterUtils';
import { HomePageCtx } from './content-home-page';
import { RUMEventTracking, convertFilterLabelToId } from '../../monitoring';

const MAX_NUMBER_OF_FILTER = 8;
enum FilterVisibilityType {
  ViewMore = 'View more',
  ViewLess = 'View less',
}

const NewContentFilters = () => {
  const { setFilterOptions, filterOptions } = useContext(HomePageCtx);
  const [filterVisibilityMap, setFilterVisibilityMap] = useState(
    new Map<string, FilterVisibilityType>(),
  );

  const clearFilters = () => {
    Object.values(filterOptions).forEach((filter) => {
      if (filter) {
        filter.expanded = true;
        Object.values(filter.options).forEach((option) => (option.selected = false));
      }
    });
    setFilterOptions({ ...filterOptions });
  };

  const expandAll = () => {
    Object.values(filterOptions).forEach((filter) => {
      if (filter) {
        filter.expanded = true;
      }
    });
    setFilterOptions({ ...filterOptions });
  };

  // @ts-expect-error TS7006 - strict migration
  const expandSection = (filter: string, event) => {
    event.preventDefault();
    // @ts-expect-error TS2532 - strict migration
    filterOptions[filter].expanded = event.detail.expanded;
    setFilterOptions({ ...filterOptions });
  };

  const selectDeselectFilter = (
    filterSectionId: string,
    filterOptionKey: string,
    event: CustomEvent<CheckboxProps.ChangeDetail>,
  ) => {
    const selected = event.detail.checked;
    const filterSection = filterOptions[filterSectionId];

    // @ts-expect-error TS18048 - strict migration
    if (filterSection.options[filterOptionKey]) {
      // @ts-expect-error TS18048 - strict migration
      filterSection.options[filterOptionKey].selected = selected;
      setFilterOptions({ ...filterOptions });
    }
  };

  const renderOptionsToCheckbox = (filterSectionId: string, filter: Filter) => {
    const { options, displayCount } = filter;
    let filterOptionsKeys = getFilterOptionsKeys(options);
    filterOptionsKeys = sortPublicationFilter(filter, filterOptionsKeys, filterSectionId);
    return filterOptionsKeys.map((optionKey, index) => {
      const filterOption = options[optionKey];
      const count = displayCount ? `(${filterOption?.count})` : null;
      const padding: BoxProps.Spacing = [
        filterOptionToLabelMap.get(FilterCategory.RequireCode),
      ].includes(filterOption?.label)
        ? { left: 'xxl' }
        : { left: 'l' };
      const filterCheckBox = (
        <RUMEventTracking
          // @ts-expect-error TS2345 - strict migration
          id={`home-page-content-filter-${convertFilterLabelToId(filter?.label)}-${convertFilterLabelToId(filterOption?.label)}`}
          key={filterOption?.label}
        >
          <Box padding={{ ...padding, bottom: 'xxs' }} key={optionKey}>
            <Checkbox
              key={optionKey}
              // @ts-expect-error TS2322 - strict migration
              checked={filterOption?.selected}
              // @ts-expect-error TS2322 - strict migration
              onChange={selectDeselectFilter.bind(this, filterSectionId, optionKey)}
            >
              {filterOption?.label} {count}
            </Checkbox>
          </Box>
        </RUMEventTracking>
      );
      const currentVisibility = filterVisibilityMap.get(filterSectionId);
      const showViewMore =
        currentVisibility === FilterVisibilityType.ViewMore || !currentVisibility;
      return showViewMore && index >= MAX_NUMBER_OF_FILTER ? null : filterCheckBox;
    });
  };

  const handleViewMoreFilterClick = (
    filterSectionId: string,
    event: CustomEvent<ButtonProps.ClickDetail>,
  ) => {
    event.preventDefault();
    const currentVisibility = filterVisibilityMap.get(filterSectionId);
    switch (currentVisibility) {
      case FilterVisibilityType.ViewMore:
        filterVisibilityMap.set(filterSectionId, FilterVisibilityType.ViewLess);
        break;
      case FilterVisibilityType.ViewLess:
        filterVisibilityMap.set(filterSectionId, FilterVisibilityType.ViewMore);
        break;
      default:
        filterVisibilityMap.set(filterSectionId, FilterVisibilityType.ViewLess);
        break;
    }
    setFilterVisibilityMap(new Map(filterVisibilityMap));
  };

  const renderViewVisibilityOption = (filterOptionsKeys: string[], filterSectionId: string) => {
    const showVisibilityOption = filterOptionsKeys.length > MAX_NUMBER_OF_FILTER;
    if (!showVisibilityOption) {
      return null;
    }
    const currentVisibility = filterVisibilityMap.get(filterSectionId);
    const showViewMore = currentVisibility === FilterVisibilityType.ViewMore || !currentVisibility;
    return (
      <div
        // @ts-expect-error TS2322 - strict migration
        onClick={handleViewMoreFilterClick.bind(this, filterSectionId)}
        className="home-page-filter-view-more"
      >
        <Icon name={showViewMore ? 'treeview-expand' : 'treeview-collapse'} variant="link" />
        <Box variant="span" color="text-status-info" fontSize="body-s" padding={{ left: 'xs' }}>
          {showViewMore ? FilterVisibilityType.ViewMore : FilterVisibilityType.ViewLess}
        </Box>
      </div>
    );
  };

  const renderFilterToSections = () => {
    return Object.keys(filterOptions).map((filterSectionId) => {
      const filter = filterOptions?.[filterSectionId];
      const filterOptionsKeys = getFilterOptionsKeys(filter?.options || {});
      return filter && !filter.hide && Object.keys(filter.options).length ? (
        <ExpandableSection
          variant={'navigation'}
          key={filterSectionId}
          expanded={filter.expanded}
          onChange={expandSection.bind(this, filter.filterCategory)}
          headerText={filter.label}
        >
          <Box padding={{ top: 'xs', bottom: 'xs' }}>
            {renderOptionsToCheckbox(filterSectionId, filter)}
            {renderViewVisibilityOption(filterOptionsKeys, filterSectionId)}
          </Box>
        </ExpandableSection>
      ) : null;
    });
  };

  return <>{renderFilterToSections()}</>;
};

export default NewContentFilters;
